// src/components/modals/UserRegistrationModal.tsx
import TosPdfViewerModal from '@/components/modals/user/TosPdfViewerModal';
import { AuthContext } from '@/context/AuthContext';
import ConductionAPI from '@/javascript/conduction-api';
import { Utils } from '@/javascript/utils';
import {
    Button,
    Checkbox,
    Group,
    Loader,
    PasswordInput,
    Stack,
    Text,
    TextInput
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertCircle, IconAt, IconLock, IconUser } from '@tabler/icons-react';
import { useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ConductionModal from '../ConductionModal';

const CLASSROOM_ACCESS_CODE_LENGTH = 8;

type UserRegistrationModalProps = {
    opened: boolean;
    onClose: () => void;
};

const UserRegistrationModal: React.FC<UserRegistrationModalProps> = ({
    opened,
    onClose,
}) => {
    const [isStudentAccessCode, setIsStudentAccessCode] = useState(false);
    const [isClassroomAdminAccessCode, setIsClassroomAdminAccessCode] = useState(false);
    const [classroomName, setClassroomName] = useState<string | null>(null);
    const [searchParams] = useSearchParams();
    // The default access code can be in a search param or it can be the final segment of the path (to allow for link sharing without query params)
    let accessCode = searchParams.get('accessCode') || window.location.pathname.split('/').pop() || null;
    if (accessCode && (accessCode.length !== CLASSROOM_ACCESS_CODE_LENGTH || accessCode === 'guest-login' || accessCode === 'register')) {
        accessCode = null;
    }

    const handleAccessCodeChange = () => {
        if (accessCode === null || (accessCode.length !== CLASSROOM_ACCESS_CODE_LENGTH)) return;

        if (accessCode.length === CLASSROOM_ACCESS_CODE_LENGTH) {
            ConductionAPI.validateClassroomAccessCode(accessCode.toUpperCase() as string)
                .then((validation) => {
                    if (validation.isValid) {
                        if (validation.isLimitReached) {
                            Utils.displayNotification("Invalid Code", "Unfortunately, the usage limit on this access code has been reached.", "red", <IconAlertCircle size={16} />);
                        } else {
                            setClassroomName(validation.classroomName ?? '');
                            setIsStudentAccessCode(validation.isStudentAccessCode);
                            setIsClassroomAdminAccessCode(validation.isClassroomAdminAccessCode);
                        }
                    } else {
                        Utils.displayNotification("Invalid Code", "The access code provided is invalid.", "red", <IconAlertCircle size={16} />);
                    }
                }
                );
        }
    };

    useEffect(() => handleAccessCodeChange(), [accessCode]);

    const session = useContext(AuthContext);
    if (session === undefined) {
        throw new Error('Component must be a child of sessionContext.');
    }

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [tosViewerOpen, setTosViewerOpen] = useState(false);

    const form = useForm({
        initialValues: {
            username: '',
            email: '',
            firstName: '',
            lastName: '',
            password: '',
            confirmPassword: '',
            tosAgreed: false,
        },
        validate: {
            username: (value: string) =>
                value.trim().length === 0 ? 'Username is required' : null,
            email: (value: string) =>
                !/^\S+@\S+\.\S+$/.test(value) ? 'Invalid email address' : null,
            firstName: (value: string) =>
                value.trim().length === 0 ? 'First name is required' : null,
            lastName: (value: string) =>
                value.trim().length === 0 ? 'Last name is required' : null,
            password: (value: string) => {
                if (value.length < 6 || value.length > 30)
                    return 'Password must be between 6 and 30 characters';
                if (!/[a-z]/.test(value)) return 'Password must contain a lowercase letter';
                if (!/[A-Z]/.test(value)) return 'Password must contain an uppercase letter';
                if (!/[0-9]/.test(value)) return 'Password must contain a number';
                if (!/[^a-z\d]/i.test(value))
                    return 'Password must contain a special character';
                return null;
            },
            confirmPassword: (value: string, values) =>
                value !== values.password ? 'Passwords do not match' : null,
            tosAgreed: (value: boolean) =>
                !value ? 'You must agree to the Terms of Service to register an account.' : null,
        },
    });

    const handleRegister = async () => {
        if (form.validate().hasErrors) {
            return;
        }

        const notificationId = Utils.displayNotification(
            'Creating Account',
            'This may take a few moments...',
            'blue',
            'loading'
        );

        setIsSubmitting(true);
        try {
            let registrationResult;
            if (isStudentAccessCode) {
                registrationResult = await ConductionAPI.registerStudent(
                    form.values.username,
                    accessCode!,
                    form.values.password,
                    form.values.confirmPassword
                );
            } else if (isClassroomAdminAccessCode) {
                registrationResult = await ConductionAPI.registerClassroomAdmin(
                    form.values.username,
                    accessCode!,
                    form.values.email,
                    form.values.password,
                    form.values.confirmPassword
                );
            } else {
                registrationResult = await ConductionAPI.registerUser(
                    form.values.username,
                    form.values.email,
                    form.values.firstName,
                    form.values.lastName,
                    form.values.password,
                    form.values.confirmPassword
                );
            }

            if (registrationResult) {
                Utils.updateNotification(
                    notificationId,
                    'Account Created',
                    null,
                    'green',
                    <IconUser size={16} />
                );

                // Auto-login
                const automaticallyAgreeTOS = session.currentTos != null && form.values.tosAgreed;
                const loggedIn = await session.login(
                    form.values.username,
                    form.values.password,
                    false,
                    automaticallyAgreeTOS
                );

                if (loggedIn) {
                    onClose();
                }
            } else {
                Utils.updateNotification(
                    notificationId,
                    'Username or Email Taken',
                    'The entered username or email may already be in use. Try a different one.',
                    'red',
                    <IconAlertCircle size={16} />,
                    true,
                    10000
                );
            }
        } catch (error) {
            Utils.updateNotification(
                notificationId,
                'Registration Error',
                'An error occurred during account creation. Please try again later.',
                'red',
                <IconAlertCircle size={16} />,
                true,
                false
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    useEffect(() => {
        if (session.currentTos == null) {
            session.fetchCurrentTos();
        }
    }, [session.user?.agreedToCurrentTos, session.currentTos, session]);

    return (
        <>
            {/* TOS viewer can live outside to avoid scroll conflicts */}
            <TosPdfViewerModal opened={tosViewerOpen} onClose={() => setTosViewerOpen(false)} />

            <ConductionModal
                opened={opened}
                onClose={onClose}
                withCloseButton
                centered
                keepMounted
                size="80%"
                closeOnClickOutside={!isSubmitting}
                closeOnEscape={!isSubmitting}
                title={
                    classroomName ? (
                        `Welcome! Now let's create your ${classroomName} ${isStudentAccessCode ? 'student' : isClassroomAdminAccessCode ? 'instructor' : 'admin'} account.`
                    ) : (
                        "Create a Conduction Account"
                    )
                }
                styles={{
                    content: {
                        maxWidth: 1000,
                        marginInline: 'auto',
                    },
                }}
            >
                <form>
                    <Stack gap="md">
                        <TextInput
                            label={
                                <Group gap={6}>
                                    <IconUser size={18} />
                                    <Text>Username</Text>
                                    <Text c="dimmed" fz="sm">
                                        (must be unique, used for signing in)
                                    </Text>
                                </Group>
                            }
                            {...form.getInputProps('username')}
                            required
                            withAsterisk={false}
                            disabled={isSubmitting}
                        />

                        <TextInput
                            label={
                                <Group gap={6}>
                                    <IconAt size={18} />
                                    <Text>Email</Text>
                                    <Text c="dimmed" fz="sm">
                                        (for notifications about the music videos we will record for you)
                                    </Text>
                                </Group>
                            }
                            {...form.getInputProps('email')}
                            required
                            withAsterisk={false}
                            disabled={isSubmitting}
                        />

                        <Group grow wrap="wrap">
                            <TextInput
                                label={
                                    <Group gap={6}>
                                        <IconUser size={18} />
                                        <Text>First Name</Text>
                                    </Group>
                                }
                                {...form.getInputProps('firstName')}
                                required
                                withAsterisk={false}
                                disabled={isSubmitting}
                            />

                            <TextInput
                                label={<Text>Last Name</Text>}
                                {...form.getInputProps('lastName')}
                                required
                                withAsterisk={false}
                                disabled={isSubmitting}
                            />
                        </Group>

                        <PasswordInput
                            label={
                                <Group gap={6}>
                                    <IconLock size={18} />
                                    <Text>Password</Text>
                                </Group>
                            }
                            {...form.getInputProps('password')}
                            required
                            withAsterisk={false}
                            description="At least 6 characters, including uppercase, lowercase, a number, and a special character."
                            disabled={isSubmitting}
                        />

                        <PasswordInput
                            label={
                                <Group gap={6}>
                                    <IconLock size={18} />
                                    <Text>Confirm Password</Text>
                                </Group>
                            }
                            {...form.getInputProps('confirmPassword')}
                            required
                            withAsterisk={false}
                            disabled={isSubmitting}
                        />

                        {session.currentTos == null ? (
                            <Loader />
                        ) : (
                            <Stack gap="xs">
                                <Text>Please review our current Terms of Service.</Text>

                                <Group gap="sm" wrap="wrap">
                                    <Button variant="light" onClick={() => setTosViewerOpen(true)}>
                                        View Terms
                                    </Button>

                                    <Checkbox
                                        label="I agree to the Terms of Service"
                                        {...form.getInputProps('tosAgreed', { type: 'checkbox' })}
                                        disabled={isSubmitting || session.currentTos == null}
                                        required
                                    />
                                </Group>
                            </Stack>
                        )}

                        <Group justify="flex-end" mt="xs">
                            <Button
                                variant="subtle"
                                onClick={onClose}
                                disabled={isSubmitting}
                            >
                                Cancel
                            </Button>

                            <Button onClick={handleRegister} radius="xl" size="sm" loading={isSubmitting}>
                                Register
                            </Button>
                        </Group>
                    </Stack>
                </form>
            </ConductionModal >
        </>
    );
};

export default UserRegistrationModal;
